import { Link } from "react-router-dom";
import { Check, Gift, GraduationCap, KeyRound, Loader2, Sparkles, Tag } from "lucide-react";
import { formatPrice } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { useBundleList, useBuyBundle } from "@/hooks/useBundles";

export function BundleCard({ bundle, index }) {
  const { currency } = useStore();
  const buy = useBuyBundle();
  return (
    <article
      data-testid={`bundle-card-${bundle.slug}`}
      style={{ animationDelay: `${index * 60}ms` }}
      className="group flex animate-fade-up flex-col overflow-hidden rounded-3xl border border-line bg-white shadow-[0_10px_40px_rgba(0,0,0,0.06)] transition-transform duration-300 hover:-translate-y-1.5"
    >
      <div className="relative overflow-hidden">
        {bundle.image_url ? (
          <img src={bundle.image_url} alt={bundle.title} className="aspect-[16/9] w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex aspect-[16/9] w-full items-center justify-center bg-gradient-to-br from-brand to-brand-dark text-white"><Gift className="h-12 w-12 opacity-80" /></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        {bundle.badge && (
          <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-brand px-3 py-1 text-[11px] font-black uppercase tracking-wide text-white">
            <Sparkles className="h-3.5 w-3.5" /> {bundle.badge}
          </span>
        )}
        {bundle.discount_percent > 0 && (
          <span className="absolute right-4 top-4 rounded-full bg-rose-500 px-3 py-1 text-xs font-black text-white">-{bundle.discount_percent}%</span>
        )}
        <div className="absolute bottom-3 left-4 right-4">
          <h3 className="font-display text-xl font-black text-white drop-shadow">{bundle.title}</h3>
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <p className="text-sm text-slate-500">{bundle.description}</p>

        <div className="mt-4 space-y-2">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">Ce pack inclut</p>
          {bundle.products.map((p) => (
            <div key={p.id} className="flex items-center gap-2.5 rounded-xl border border-line p-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-100 text-slate-500"><KeyRound className="h-4 w-4" /></span>
              <span className="flex-1 truncate text-sm font-semibold text-ink">{p.name}</span>
              <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold text-slate-500">Licence</span>
            </div>
          ))}
          {bundle.formations.map((f) => (
            <div key={f.id} className="flex items-center gap-2.5 rounded-xl border border-line p-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-light text-brand"><GraduationCap className="h-4 w-4" /></span>
              <span className="flex-1 truncate text-sm font-semibold text-ink">{f.name}</span>
              <span className="rounded-md bg-brand-light px-1.5 py-0.5 text-[10px] font-bold text-brand">Formation</span>
            </div>
          ))}
        </div>

        <div className="mt-auto pt-5">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-xs text-slate-400 line-through">{formatPrice(bundle.value, currency)}</p>
              <p className="font-display text-2xl font-black text-brand" data-testid={`bundle-price-${bundle.slug}`}>{formatPrice(bundle.price, currency)}</p>
            </div>
            {bundle.savings > 0 && (
              <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
                <Tag className="h-3.5 w-3.5" /> Économisez {formatPrice(bundle.savings, currency)}
              </span>
            )}
          </div>
          <button
            onClick={() => buy(bundle)}
            data-testid={`bundle-buy-${bundle.slug}`}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-brand px-4 py-3.5 text-sm font-bold text-white btn-glow transition-[background-color,transform] duration-200 hover:bg-brand-dark active:scale-[0.98]"
          >
            <Gift className="h-5 w-5" /> Acheter le pack
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Offres() {
  const { data, isLoading } = useBundleList();
  const items = data?.items || [];

  return (
    <div className="bg-surface pb-16">
      <section className="relative overflow-hidden bg-night py-14 lg:py-20">
        <div className="hero-grid-lines absolute inset-0 opacity-40" />
        <div className="container-it relative text-center">
          <span className="label-eyebrow text-brand-light">Offres groupées</span>
          <h1 className="mt-3 font-display text-4xl font-black text-white sm:text-5xl lg:text-6xl">Packs licence + formation</h1>
          <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">
            Combinez vos logiciels et vos formations dans un seul pack et économisez sur le prix total.
          </p>
        </div>
      </section>

      <div className="container-it -mt-6">
        {isLoading ? (
          <div className="flex h-60 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-line bg-white py-20 text-center">
            <Gift className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-3 font-display text-lg font-bold text-ink">Aucune offre pour le moment</p>
            <Link to="/formations" className="mt-4 inline-block rounded-full bg-brand px-6 py-2.5 text-sm font-bold text-white">Voir les formations</Link>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {items.map((b, i) => <BundleCard key={b.id} bundle={b} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
